// The two card rows of A3 and A4 (03/Oct/26):
//   row 1 — status: Open · Critical · Closed · Overdue · Total · Total incl. voided
//   row 2 — open issues per discipline, with empty dashed slots up to 7 disciplines
// On A4 the cards are the filters (code list #4): pass onStatus / onDiscipline; the chosen card is outlined.
import type { Discipline, IssueCounts, SignoffState } from "../../../core/api/types";
import { MAX_DISCIPLINES, STATUS_CARDS, type StatusKey } from "../logic";

export function StatusCards({ stats, selected, onSelect, scope }: { stats: IssueCounts; selected?: StatusKey | ""; onSelect?: (k: StatusKey) => void; scope: string }) {
  return (
    <div className="cards6" data-testid={`${scope}-status-cards`}>
      {STATUS_CARDS.map((c) => {
        const body = <><p className="k">{c.label}</p><p className="v">{stats[c.key]}</p></>;
        return onSelect
          ? <button key={c.key} className={`stat${selected === c.key ? " picked" : ""}`} style={{ background: c.bg, color: c.fg }} data-stat={c.key}
              aria-pressed={selected === c.key} onClick={() => onSelect(c.key)}>{body}</button>
          : <div key={c.key} className="stat" style={{ background: c.bg, color: c.fg }} data-stat={c.key}>{body}</div>;
      })}
    </div>
  );
}

export function DisciplineCards({ stats, disciplines, selected, onSelect }: { stats: IssueCounts; disciplines: Discipline[]; selected?: string; onSelect?: (code: string) => void }) {
  const max = Math.max(1, ...disciplines.map((d) => stats.open_by_discipline[d.code] ?? 0));
  const slots = Math.max(0, MAX_DISCIPLINES - disciplines.length);
  return (
    <div className="cards7">
      {disciplines.map((d) => {
        const n = stats.open_by_discipline[d.code] ?? 0;
        const body = <><p className="k">{d.code} open</p><p className="v">{n}</p><div className="bar"><div style={{ width: `${Math.round((n / max) * 100)}%`, background: d.colour }} /></div></>;
        const style = { background: d.colour + "1a", color: d.colour };
        return onSelect
          ? <button key={d.code} className={`stat${selected === d.code ? " picked" : ""}`} style={style} data-stat={d.code} aria-pressed={selected === d.code} onClick={() => onSelect(d.code)}>{body}</button>
          : <div key={d.code} className="stat" style={style} data-stat={d.code}>{body}</div>;
      })}
      {Array.from({ length: slots }, (_, k) => <div key={`slot${k}`} className="stat slot"><p className="k">–</p><p className="v">–</p></div>)}
    </div>
  );
}

/** A3 Sign-off column: only the version name, coloured by status (green signed off · amber awaiting · grey published, not sent).
 *  With onOpen it is a button that opens the A3.3 Sign-off pop-up under it (Part 5). */
export function SignoffChip({ s, onOpen }: { s: SignoffState; onOpen?: (el: HTMLElement) => void }) {
  if (s.state === "none" || !s.version) return <span className="muted">–</span>;
  const title = { signed: "Signed off", awaiting: "Awaiting sign-off (exported with signing page)", published: "Published, not sent for sign-off" }[s.state];
  return onOpen
    ? <button type="button" className={`so-chip ${s.state}`} aria-label={`${title} ${s.version} — open sign-off`} data-signoff={s.state}
        onClick={(e) => onOpen(e.currentTarget)}>{s.version}</button>
    : <span className={`so-chip ${s.state}`} title={title} data-signoff={s.state}>{s.version}</span>;
}
